import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';

const NW_ENGLAND_SCHOOLS = [
  'Academy@Worden, Leyland',
  'Albany Academy, Chorley',
  'All Hallows Catholic High School, Penwortham',
  'Archbishop Temple School, Preston',
  'Ashton Community Science College, Preston',
  'Balshaw\'s Church of England High School, Leyland',
  'Bishop Rawstorne Church of England Academy, Croston',
  'Blackburn Central High School, Blackburn',
  'Broughton High School, Preston',
  'Brownedge St Mary\'s Catholic High School, Bamber Bridge',
  'Burscough Priory Academy, Burscough',
  'Canon Slade School, Bolton',
  'Christ the King Catholic High School, Preston',
  'Corpus Christi Catholic High School, Fulwood',
  'Deanery High School, Wigan',
  'Fulwood Academy, Preston',
  'Holy Cross Catholic High School, Chorley',
  'Hutton Grammar School, Hutton',
  'Lathom High School, Skelmersdale',
  'Lostock Hall Academy, Lostock Hall',
  'Moor Park High School, Preston',
  'Our Lady\'s Catholic High School, Fulwood',
  'Our Lady Queen of Peace Catholic Engineering College, Skelmersdale',
  'Parklands High School, Chorley',
  'Penwortham Girls\' High School, Penwortham',
  'Penwortham Priory Academy, Penwortham',
  'Preston Muslim Girls High School, Preston',
  'Rivington and Blackrod High School, Horwich',
  'Southlands High School, Chorley',
  'St Bede\'s Catholic High School, Ormskirk',
  'St James\'s CE High School, Farnworth',
  'St Mary\'s Catholic High School, Leyland',
  'St Michael\'s CE High School, Chorley',
  'St Wilfrid\'s CE Academy, Blackburn',
  'Tarleton Academy, Tarleton',
  'Up Holland High School, Orrell',
  'Walton-le-Dale High School, Bamber Bridge',
  'Wellfield Academy, Leyland',
  'Westhoughton High School, Bolton',
  'Blackburn College, Blackburn',
  'Bolton College, Bolton',
  'Cardinal Newman College, Preston',
  'Holy Cross Sixth Form College, Bury',
  'Myerscough College, Bilsborrow',
  'Preston College, Preston',
  'Runshaw College, Leyland',
  'St John Rigby College, Wigan',
  'West Lancashire College, Skelmersdale',
  'Wigan & Leigh College, Wigan',
  'Winstanley College, Wigan',
];

export default function ProfileSchoolField({ value, onChange, error }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInputChange(e) {
    const val = e?.target?.value;
    setQuery(val);
    onChange({ target: { name: 'school', value: val } });
    if (val?.trim()?.length >= 2) {
      const filtered = NW_ENGLAND_SCHOOLS?.filter((s) =>
        s?.toLowerCase()?.includes(val?.toLowerCase())
      )?.slice(0, 8);
      setSuggestions(filtered);
      setIsOpen(filtered?.length > 0);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }

  function handleSelect(school) {
    setQuery(school);
    onChange({ target: { name: 'school', value: school } });
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
  }

  function handleKeyDown(e) {
    if (!isOpen) return;
    if (e?.key === 'ArrowDown') {
      e?.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions?.length - 1));
    } else if (e?.key === 'ArrowUp') {
      e?.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e?.key === 'Enter' && highlightedIndex >= 0) {
      e?.preventDefault();
      handleSelect(suggestions?.[highlightedIndex]);
    } else if (e?.key === 'Escape') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor="school" className="block text-sm font-medium text-foreground mb-1">
        Current School / College
        <span className="text-destructive ml-1">*</span>
      </label>
      <div className="relative">
        <input
          id="school"
          name="school"
          type="text"
          autoComplete="off"
          placeholder="e.g. Greenfield Academy, Leyland"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions?.length > 0) setIsOpen(true); }}
          maxLength={150}
          aria-autoComplete="list"
          aria-expanded={isOpen}
          aria-controls="school-suggestions-profile"
          aria-activedescendant={highlightedIndex >= 0 ? `school-option-profile-${highlightedIndex}` : undefined}
          className={`w-full px-3 py-2 pr-9 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
            error ? 'border-destructive focus:ring-destructive' : 'border-input'
          }`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
          <Icon name="School" size={16} />
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Enter your current educational institution in North West England
      </p>
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
      {isOpen && suggestions?.length > 0 && (
        <ul
          id="school-suggestions-profile"
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-56 overflow-y-auto"
        >
          {suggestions?.map((school, idx) => (
            <li
              key={school}
              id={`school-option-profile-${idx}`}
              role="option"
              aria-selected={idx === highlightedIndex}
              onMouseDown={(e) => e?.preventDefault()}
              onClick={() => handleSelect(school)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                idx === highlightedIndex ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon
                name="MapPin"
                size={13}
                color={idx === highlightedIndex ? 'currentColor' : 'var(--color-muted-foreground)'}
              />
              {school}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
